import React, { useState, useRef, useEffect } from 'react';
import { Layers, ChevronDown, Search, X } from 'lucide-react';
import { FullProject } from '../../../types';

interface SubHeaderProjectFilterDropdownProps {
  workspaceProjects?: FullProject[];
  hiddenProjectIds?: string[];
  setHiddenProjectIds?: (ids: string[]) => void;
  onOpenAnotherDropdown?: () => void;
}

export const SubHeaderProjectFilterDropdown: React.FC<SubHeaderProjectFilterDropdownProps> = ({
  workspaceProjects = [],
  hiddenProjectIds = [],
  setHiddenProjectIds = () => {},
  onOpenAnotherDropdown
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!workspaceProjects || workspaceProjects.length === 0) return null;

  // Filter out archived projects and system/communication projects
  const isSystemOrKommunikation = (p: FullProject) => {
    const id = p.id.toLowerCase();
    const name = p.name.toLowerCase();
    return id === 'kommunikation' || 
           id === 'communication' || 
           id === 'comm_internal' ||
           id === 'inbox_inforum' ||
           id === 'posteingang' ||
           id === 'inbox' ||
           name.includes('kommunikation') || 
           name.includes('communication') || 
           name.includes('posteingang') ||
           name.includes('inbox');
  };

  const eligibleProjects = workspaceProjects.filter(p => !p.archived && !isSystemOrKommunikation(p));

  // Filter projects matching search term
  const filteredProjects = eligibleProjects.filter(p => 
    p.name.toLowerCase().includes(projectSearchQuery.toLowerCase())
  );

  const activeCount = eligibleProjects.filter(p => !hiddenProjectIds.includes(p.id)).length;

  return (
    <div className="relative h-[34px] shrink-0" ref={dropdownRef}>
      <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center">
        <button 
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            onOpenAnotherDropdown?.();
          }}
          className={`px-3 h-full rounded-lg flex items-center justify-center gap-1.5 transition-all text-[10px] font-bold uppercase tracking-widest ${
            isOpen || hiddenProjectIds.length > 0
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-black/5 font-black' 
              : 'text-slate-400 hover:text-slate-500'
          }`}
          title="Projekte anzeigen/ausblenden"
        >
          <Layers size={14} />
          <span>Projekte ({activeCount}/{eligibleProjects.length})</span>
          <ChevronDown size={10} className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-505' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-[100] p-2 flex flex-col gap-1.5 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
          {/* Title */}
          <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-850/50 mb-1 flex items-center justify-between">
            <span>Projekte filtern</span>
            {hiddenProjectIds.length > 0 && (
              <span className="text-amber-500 dark:text-amber-400 font-extrabold normal-case tracking-normal">Filter aktiv</span>
            )}
          </div>

          {/* Quick Actions */}
          <div className="flex gap-1.5 px-1 pb-1 border-b border-slate-100 dark:border-slate-850/30">
            <button
              type="button"
              onClick={() => setHiddenProjectIds([])}
              className="flex-1 py-1 rounded-lg bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-800 text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 text-center border border-slate-150 dark:border-slate-800 transition-all active:scale-[0.98]"
            >
              Alle an
            </button>
            <button
              type="button"
              onClick={() => setHiddenProjectIds(eligibleProjects.map(p => p.id))}
              className="flex-1 py-1 rounded-lg bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-800 text-[9px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 text-center border border-slate-150 dark:border-slate-800 transition-all active:scale-[0.98]"
            >
              Alle aus
            </button>
          </div>

          {/* Project Search Input */}
          <div className="relative px-2 py-1.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-150 dark:border-slate-800 flex items-center gap-1.5 mx-1">
            <Search size={11} className="text-slate-400 shrink-0" />
            <input
              type="text"
              placeholder="Projekt suchen..."
              value={projectSearchQuery}
              onChange={(e) => setProjectSearchQuery(e.target.value)}
              className="bg-transparent text-[10px] font-bold text-slate-700 dark:text-slate-200 outline-none w-full placeholder-slate-450"
            />
            {projectSearchQuery && (
              <button 
                type="button"
                onClick={() => setProjectSearchQuery('')} 
                className="text-slate-400 hover:text-slate-650 dark:hover:text-slate-200"
              >
                <X size={11} />
              </button>
            )}
          </div>

          {/* Project Checkbox List */}
          <div className="flex flex-col gap-1 max-h-56 overflow-y-auto pr-1 custom-scrollbar px-1 mt-1">
            {filteredProjects.length === 0 ? (
              <div className="px-3 py-4 text-center text-[10px] text-slate-400 italic font-medium">
                Keine Projekte gefunden
              </div>
            ) : (
              filteredProjects.map((p) => {
                const isVisible = !hiddenProjectIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      if (isVisible) {
                        setHiddenProjectIds([...hiddenProjectIds, p.id]);
                      } else {
                        setHiddenProjectIds(hiddenProjectIds.filter(id => id !== p.id));
                      }
                    }}
                    className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-left transition-all duration-200 group/item border ${
                      isVisible 
                        ? 'hover:bg-slate-50 dark:hover:bg-slate-900 border-transparent text-slate-750 dark:text-slate-300' 
                        : 'bg-slate-50/50 dark:bg-slate-900/10 border-slate-100 dark:border-slate-900/50 text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${p.color || 'bg-blue-500'}`} />
                      <span className="text-[10px] font-bold uppercase tracking-wider truncate">{p.name}</span>
                    </div>
                    
                    {/* Checkbox custom indicator */}
                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0 ${
                      isVisible 
                        ? 'bg-indigo-600 border-indigo-650 text-white' 
                        : 'border-slate-300 dark:border-slate-700 text-transparent'
                    }`}>
                      <svg className="w-2.5 h-2.5 stroke-current fill-none stroke-[3]" viewBox="0 0 24 24">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
